import { Box, useMediaQuery, useTheme } from "@mui/material";
import TopMenu from "./components/TopMenu";
import Footer from "./components/Footer";
import { Outlet, useLocation } from "react-router-dom";
import { useLayoutEffect, useState } from "react";
import Sidebar from "./components/Sidebar";
import { Toaster } from "react-hot-toast";
import { isAuthorizedToViewContent } from "@/helpers/auth";
import { useSelector } from "react-redux";
import userSelector from "@/store/selectors/userSelector";
import SidebarAdmin from "./components/SidebarAdmin";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { csCZ } from "@mui/x-date-pickers/locales";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/cs";
import CookieConsent from "react-cookie-consent";
import { ROUTES } from "@/routes";
import { CHANGE_LOG_ENTRIES } from "@/components/changeLogEntries";
import UpdateAnnouncementDialog from "./components/UpdateAnnouncementDialog";

dayjs.locale("cs");

const UPDATE_ANNOUNCEMENT_STORAGE_KEY = "update-announcement-last-seen";
const LATEST_CHANGE_LOG_VERSION = CHANGE_LOG_ENTRIES[0]?.date ?? "unknown";

const ScrollFix = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();

  useLayoutEffect(() => {
    // Scroll to the top of the page when the route changes
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname]);

  return children;
};

const Layout = () => {
  const theme = useTheme();
  const [footerHeaderHeight, setFooterHeaderHeight] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    return localStorage.getItem("sidebarCollapsed") === "true";
  });
  const [isUpdateAnnouncementOpen, setIsUpdateAnnouncementOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(prev => {
      const newState = !prev;
      localStorage.setItem("sidebarCollapsed", String(newState));
      return newState;
    });
  };
  const location = useLocation();
  const user = useSelector(userSelector);
  const isClient = user?.type === "client";
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  const isHomepage = location.pathname === ROUTES.HOME;

  useLayoutEffect(() => {
    const footerElement = document.getElementById("footer");
    const headerElement = document.getElementById("header");

    const updateLayoutHeights = () => {
      setFooterHeaderHeight(footerElement?.offsetHeight ?? 0);
      setHeaderHeight(headerElement?.offsetHeight ?? 0);
    };

    updateLayoutHeights();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateLayoutHeights)
        : null;

    if (footerElement && resizeObserver) resizeObserver.observe(footerElement);
    if (headerElement && resizeObserver) resizeObserver.observe(headerElement);

    window.addEventListener("resize", updateLayoutHeights);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateLayoutHeights);
    };
  }, [location.pathname]);

  const isPrivatePage =
    location.pathname.includes("client") || location.pathname.includes("admin");
  const isAuthorized = isAuthorizedToViewContent("private", user);

  const showSidebar = isPrivatePage && isAuthorized && !isMediumScreen && (!isClient || (user?.verifiedByAdmin && user.verifiedEmail));

  useLayoutEffect(() => {
    if (!isPrivatePage || !isAuthorized || !user?.id) return;

    const lastSeenVersion = localStorage.getItem(UPDATE_ANNOUNCEMENT_STORAGE_KEY);
    if (lastSeenVersion === LATEST_CHANGE_LOG_VERSION) return;

    setIsUpdateAnnouncementOpen(true);
  }, [isAuthorized, isPrivatePage, user?.id]);

  const handleCloseUpdateAnnouncement = () => {
    localStorage.setItem(UPDATE_ANNOUNCEMENT_STORAGE_KEY, LATEST_CHANGE_LOG_VERSION);
    setIsUpdateAnnouncementOpen(false);
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={"cs"}
      localeText={
        csCZ.components.MuiLocalizationProvider.defaultProps.localeText
      }
    >
      <>
        <Toaster position="top-center" toastOptions={{ duration: 4000 }} />
        <TopMenu />
        <Box
          sx={{
            display: "flex",
            minHeight: showSidebar
              ? `calc(100vh - ${footerHeaderHeight}px)`
              : "auto",
            backgroundColor: "background.default",
          }}
        >
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              paddingTop: `${isHomepage ? 0 : headerHeight}px`,
              width: "100%",
              minHeight: 500,
              display: "flex",
              }}
              >
            {showSidebar && (isClient ? 
              <Sidebar isCollapsed={isSidebarCollapsed} onToggle={toggleSidebar} /> : 
              <SidebarAdmin isCollapsed={isSidebarCollapsed} onToggle={toggleSidebar} />
            )}
            <Box sx={{
              flex: 1,
              minWidth: 0,
              width: '100%',
            }}>
              <ScrollFix>
                <Outlet />
              </ScrollFix>
            </Box>
          </Box>
        </Box>
        <Footer />
        <UpdateAnnouncementDialog
          open={isUpdateAnnouncementOpen}
          onClose={handleCloseUpdateAnnouncement}
        />
        <CookieConsent
          style={{
            zIndex: 9999,
            backgroundColor: theme.palette.primary.main,
            boxShadow: "0 0 5px rgba(255,255,255,1)"            
          }}
          buttonText="Beru na vědomí"
          buttonStyle={{
            backgroundColor: theme.palette.background.paper,
            borderRadius: '25px',
            color: theme.palette.text.primary,
          }}
        >
          Tyto webové stránky používají k poskytování svých služeb technické cookies. Technické cookies o Vás nesbírají žádné analytické informace, jsou nezbytné pro správné fungování webu.
        </CookieConsent>
      </>
    </LocalizationProvider>
  );
};

export default Layout;
